import { NgClass } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from '@openng/optimus-ui/button';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MessageModule } from '@openng/optimus-ui/message';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { SelectModule } from '@openng/optimus-ui/select';
import { TagModule } from '@openng/optimus-ui/tag';
import { injectParams } from 'ngxtension/inject-params';
import { map, switchMap } from 'rxjs';

import { PipelineService } from '@app/modules/assets/services/pipeline-builder.service';
import { RiskAssessmentDetailsComponent } from '@app/modules/management/components/risk-assessment/risk-assessment-details/risk-assessment-details.component';
import {
    getRiskAssessmentForAPI,
    getRiskAssessmentForm,
    type RiskAssessmentAdapter,
} from '@app/modules/management/models/risk-assessment.model';
import { RiskAssessmentService } from '@app/modules/management/services/risk-assessment.service';
import { DialogBoxComponent } from '@app/shared/components/dialog-box/dialog-box.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { OBJECTS, SKELETON_TYPES } from '@app/shared/constants/shared.constant';
import { ApiErrorService } from '@app/shared/services/api-error.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { FormHelper } from '@app/shared/utils/form-helper';
import { parseAsNumber } from '@app/shared/utils/helpers';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import { RouterHelper } from '@app/shared/utils/router-helper';

@Component({
    selector: 'cor-pipeline-risk-assessment',
    templateUrl: './pipeline-risk-assessment.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        ButtonModule,
        InputTextModule,
        MessageModule,
        RadioButtonModule,
        SelectModule,
        SkeletonComponent,
        NoDataComponent,
        DialogBoxComponent,
        InputContainerComponent,
        RiskAssessmentDetailsComponent,
        TagModule,
        NgClass,
    ],
})
export class PipelineRiskAssessmentComponent implements OnInit {
    readonly id = input(undefined, { transform: parseAsNumber });
    protected readonly currentProject = injectParams<string>('projectSlug');
    readonly transactionId = input(undefined, { transform: parseAsNumber });
    readonly showCreateOnly = input(false);

    protected readonly SKELETON_TYPES = SKELETON_TYPES;

    private readonly pipelineService = inject(PipelineService);
    private readonly riskAssessmentService = inject(RiskAssessmentService);
    protected readonly apiErrorService = inject(ApiErrorService);
    protected readonly destroyRef = inject(DestroyRef);
    private readonly toast = inject(ToastNotificationService);

    protected readonly item = this.riskAssessmentService.item;
    protected readonly isLoading = signal(true);
    protected readonly isNotFound = signal(false);
    protected readonly create = {
        isVisible: signal(false),
        isLoading: signal(false),
        isSaving: signal(false),
        copy: signal(false),
        versionList: signal<{ label: string; value: number }[]>([]),
        sourcePipelineVersionId: signal<number>(null),
        form: getRiskAssessmentForm({} as RiskAssessmentAdapter, []),
        onShow: () => {
            this.create.isLoading.set(true);
            this.pipelineService
                .getById(this.id(), { simple: true })
                .pipe(
                    switchMap(({ result: pipeline }) =>
                        this.pipelineService
                            .getAll({
                                parentId: pipeline.parentId || pipeline.id,
                                simple: true,
                            })
                            .pipe(
                                map(({ result }) => ({
                                    pipeline,
                                    versionList: result
                                        .filter(version => version.version < pipeline.version)
                                        .map(version => ({
                                            label: `v${version.version}`,
                                            value: version.id,
                                        }))
                                        ?.reverse(),
                                })),
                            ),
                    ),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(({ pipeline, versionList }) => {
                    this.create.form = getRiskAssessmentForm(
                        {
                            name: `${pipeline.name} Risk Assessment`,
                        } as RiskAssessmentAdapter,
                        [],
                    );
                    this.create.versionList.set(versionList);
                    if (versionList.length > 0) {
                        this.create.copy.set(true);
                        this.create.sourcePipelineVersionId.set(versionList?.[0]?.value ?? null);
                    }
                    this.create.isVisible.set(true);
                })
                .add(() => this.create.isLoading.set(false));
        },
        onHide: () => {
            this.create.isVisible.set(false);
            this.create.form?.reset();
            this.create.copy.set(false);
            this.create.versionList.set([]);
            this.create.sourcePipelineVersionId.set(null);
        },
        onSave: () => {
            if (this.create.form.invalid) {
                FormHelper.showFormErrors(this.create.form, this.toast);
                return;
            }

            this.create.isSaving.set(true);

            const createObs$ =
                this.create.copy() && this.create.sourcePipelineVersionId()
                    ? this.riskAssessmentService.copyFromPipelineVersion({
                          name: this.create.form.controls.name.value,
                          pipelineVersionId: this.id(),
                          sourcePipelineVersionId: this.create.sourcePipelineVersionId(),
                      })
                    : this.riskAssessmentService.create(
                          getRiskAssessmentForAPI({
                              ...this.create.form.getRawValue(),
                              pipelineVersionId: this.id(),
                              projectSlug: this.currentProject(),
                          }),
                      );

            createObs$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ({ result }) => {
                        this.item.set(result);
                        this.isNotFound.set(false);
                        this.create.onHide();
                    },
                    error: err => {
                        this.apiErrorService.handleValidationError(err, this.create.form);
                    },
                })
                .add(() => this.create.isSaving.set(false));
        },
    };

    protected readonly configurationLink = computed(() =>
        RouterHelper.buildRoute({
            object: OBJECTS.RISK_ASSESSMENT,
            id: this.item()?.id,
            tabRoutes: TAB_ROUTES.SETTINGS,
        }),
    );

    ngOnInit() {
        this.pipelineService
            .getRiskAssessment(this.id(), {
                auth: true,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                if (!result) {
                    // FIXME: This is a temporary solution to handle the case when the risk assessment is not found.
                    // We should handle this case properly.
                    this.isNotFound.set(true);
                    return;
                }

                this.item.set(result);
            })
            .add(() => this.isLoading.set(false));
    }
}
